import {useEffect, useMemo, useState} from "react";
import {CustomAction} from "@/api/services/customAction";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  SearchIcon,
  Zap,
} from "lucide-react";
import {Icon} from "@/components/ui/icon";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {Button} from "@/components/ui/Button";
import {Plus} from "lucide-react";
import {Input} from "@/components/ui/input";
import {httpModeColors} from "@/constants/apiMethods";
import CalComIcon from "@/components/icons/Cal";
import GoHighLevel from "@/components/icons/GoHightLevel";
import {useActionListManage} from "@/hooks/useActionListManage";
import {
  ActionTypeModel,
  AssistantActionAttachmentTypeModel,
  ActionRecordModel,
  APIMethodEnum,
  RealTimeBookingRecordModel,
} from "@/api/models";
import {debounce} from "lodash";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {getAction} from "@/api/services/queries";
import {actionTransform} from "@/api/transform/actions";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {useToast} from "@/hooks/useToast";

type BookingType = "calcom" | "ghl";
export interface TransferCallAction {
  name?: string;
  trigger?: string;
  phone_number?: string;
  action_id?: string;
  call_transfer_info?: {
    mode?: "warm_transfer" | "cold_transfer" | "warm_transfer_with_context";
    message?: string;
  };
  variables?: string[];
  slug?: string;
}

export interface BookingAction {
  name?: string;
  start_message?: string;
  action_id?: string;
  config?: {
    first_appt_date?: string;
    type?: BookingType;
    phone_number?: string;
  };
  body?: {
    properties?: Record<string, string>;
  };
  slug?: string;
  description?: string;
  variables?: string[];
}

const SEARCH_DEBOUNCE_TIME = 500;

const ActionsTableHeader = (actionType: ActionTypeModel) => {
  switch (actionType) {
    case ActionTypeModel.BOOKING:
      return (
        <>
          <TableHead className="w-16"></TableHead>
          <TableHead className="w-full">Calendar Event Title</TableHead>
          <TableHead className="w-40 whitespace-nowrap">Booking Date</TableHead>
        </>
      );
    case ActionTypeModel.TRANSFER:
      return (
        <>
          <TableHead className="w-40">Number to</TableHead>
          <TableHead className="w-full">
            When should the transfer take place?
          </TableHead>
        </>
      );
    case ActionTypeModel.CUSTOM:
      return (
        <>
          <TableHead className="w-16"></TableHead>
          <TableHead className="w-auto">Action Name</TableHead>
          <TableHead className="w-25">Method</TableHead>
        </>
      );
    default:
      return null;
  }
};

const ActionsTableBody = (
  actionType: ActionTypeModel,
  action: CustomAction | TransferCallAction | ActionRecordModel,
) => {
  switch (actionType) {
    case ActionTypeModel.BOOKING: {
      const bookingAction = action as RealTimeBookingRecordModel & {
        name: string;
      };
      let formattedDate = "N/A";
      const firstApptDateStr = bookingAction.config?.firstAppDate;
      if (firstApptDateStr) {
        const firstApptDate = new Date(firstApptDateStr);
        if (!isNaN(firstApptDate.getTime())) {
          formattedDate =
            firstApptDate.getDate().toString().padStart(2, "0") +
            " " +
            firstApptDate.toLocaleString("default", {month: "long"}) +
            ", " +
            firstApptDate.getFullYear();
        }
      }
      const bookingType = bookingAction.config?.type;
      return (
        <>
          <TableCell className="font-medium w-16">
            {bookingType === "calcom" ? (
              <CalComIcon
                width={20}
                height={20}
                className="text-primary-accent"
              />
            ) : (
              <GoHighLevel
                width={20}
                height={20}
                className="text-primary-accent"
              />
            )}
          </TableCell>
          <TableCell className="font-medium w-full min-w-0">
            {bookingAction.name || "N/A"}
          </TableCell>
          <TableCell className="w-40">{formattedDate || "N/A"}</TableCell>
        </>
      );
    }
    case ActionTypeModel.TRANSFER: {
      const transferAction = action as TransferCallAction;
      return (
        <>
          <TableCell className="w-40">
            {transferAction.phone_number || "N/A"}
          </TableCell>
          <TableCell className="w-full">
            <span className="line-clamp-2 overflow-hidden text-ellipsis break-words">
              {transferAction.trigger}
            </span>
          </TableCell>
        </>
      );
    }
    case ActionTypeModel.CUSTOM: {
      const customAction = action as CustomAction;
      return (
        <>
          <TableCell className="pr-0 w-16">
            <Icon className="bg-white text-label border">
              {customAction.icon ? (
                <img
                  className="aspect-square h-full w-full"
                  src={customAction.icon}
                />
              ) : (
                <Zap className="w-5 h-5" />
              )}
            </Icon>
          </TableCell>
          <TableCell className="w-auto min-w-0">
            <div className="leading-none min-w-0">
              <h3 className="text-sm text-primary font-medium truncate">
                {customAction.name}
              </h3>
              <span
                className="text-sm text-secondary truncate block"
                title={customAction.url}
              >
                {customAction.url}
              </span>
            </div>
          </TableCell>
          <TableCell className="w-25">
            <div className="flex items-center">
              <div
                className={`${httpModeColors[customAction.http_method?.toLowerCase() || "label"]} text-sm font-medium px-1.5 py-1 rounded-md`}
              >
                {customAction.http_method?.toUpperCase() || "N/A"}
              </div>
            </div>
          </TableCell>
        </>
      );
    }
    default:
      return null;
  }
};

export const ActionTable = ({
  handleActionSelect,
  onDialogClose,
  actionType,
}: {
  handleActionSelect: (action: ActionRecordModel) => void;
  onDialogClose?: () => void;
  actionType: ActionTypeModel;
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 5;
  const {setSelectedNode} = useSelectedElements();
  const [isCreatingAction, setIsCreatingAction] = useState(false);
  const {errorToast} = useToast();
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, SEARCH_DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const {data, setPagination, isFetching, isRefetching, handleCreateAction} =
    useActionListManage({
      actionType: actionType,
      paginationSize: itemsPerPage,
      attachmentType: AssistantActionAttachmentTypeModel.DURING,
      search: searchTerm,
      externalPagination: {
        pagination: {
          pageIndex: currentPage - 1,
          pageSize: itemsPerPage,
        },
        setPagination: (pagination: {pageIndex: number; pageSize: number}) => {
          setCurrentPage(pagination.pageIndex + 1);
        },
      },
    });
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setPagination({pageIndex: page - 1, pageSize: itemsPerPage});
  };

  const isLoading = isFetching || isRefetching;

  const handleActionClick = (action: ActionRecordModel) => {
    setSelectedNode(null);
    handleActionSelect(action);
    onDialogClose && onDialogClose();
  };

  return (
    <div className="my-4 px-4">
      <div className="flex items-center justify-between mb-4 gap-3">
        <Input
          containerClassName="w-full"
          startContent={<SearchIcon className="w-4 h-4 stroke-[#AAB5C5]" />}
          placeholder="Search by name or ID"
          onChange={e => {
            const value = e.target.value;
            debouncedSearch(value);
          }}
          disabled={isLoading}
        />
        {actionType === ActionTypeModel.CUSTOM && (
          <Button
            className="[&_svg]:size-5"
            onClick={async () => {
              setIsCreatingAction(true);
              const newAction = {
                title: "New Custom Action",
                description: "Call this API to do ____",
                workspaceId: FlowBuilderSettings.workspaceId,
                apiMethod: APIMethodEnum.POST,
              };
              try {
                const newActionId =
                  (await handleCreateAction(newAction)).key || "";
                const newActionData = await getAction(newActionId);

                const newActionRecord = actionTransform[
                  actionType
                ].makeRecordModel(newActionData as never);
                handleActionSelect(
                  newActionRecord as unknown as ActionRecordModel,
                );
                onDialogClose && onDialogClose();
              } catch (error) {
                console.error(error);
                errorToast("Failed to create action");
              } finally {
                setIsCreatingAction(false);
              }
            }}
          >
            {isCreatingAction ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="text-white" />
            )}
            Create New
          </Button>
        )}
      </div>
      <div className="flex flex-col">
        <Table className="w-full max-w-full table-fixed rounded-lg border">
          <TableHeader>
            <TableRow>{ActionsTableHeader(actionType)}</TableRow>
          </TableHeader>
          <TableBody className="border-b">
            {isLoading ? (
              <TableRow className="h-[405px]">
                <TableCell colSpan={3}>
                  <div className="flex justify-center items-center ">
                    <Loader2 className="size-10 animate-spin" />
                  </div>
                </TableCell>
              </TableRow>
            ) : !data || data?.records?.length === 0 ? (
              <TableRow className="h-[405px]">
                <TableCell colSpan={3}>
                  <div className="flex justify-center items-center">
                    <span className="text-sm text-secondary">
                      No actions found
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data?.records?.map((action, index) => (
                <TableRow
                  key={action.action_id + index}
                  onClick={() => {
                    handleActionClick(action);
                  }}
                  className="cursor-pointer hover:bg-muted overflow-hidden text-ellipsis break-words"
                >
                  {ActionsTableBody(actionType, action)}
                </TableRow>
              ))
            )}
          </TableBody>
          {data?.pagination?.totalCount > itemsPerPage && (
            <TableFooter className="border-b border-t-0">
              <TableRow>
                <TableCell colSpan={3}>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            if (currentPage > 1)
                              handlePageChange(currentPage - 1);
                          }}
                          isActive={currentPage > 1}
                          className={`${currentPage > 1 ? "cursor-pointer" : "pointer-events-none"}`}
                        >
                          <ChevronLeft className="h-4 w-4 text-secondary" />
                        </PaginationPrevious>
                      </PaginationItem>

                      {(() => {
                        const totalPages = Math.ceil(
                          data.pagination.totalCount / itemsPerPage,
                        );
                        const pages = [];
                        const maxVisiblePages = 1;

                        let shouldShowLeftEllipsis = false;
                        let shouldShowRightEllipsis = false;

                        for (let i = 1; i <= totalPages; i++) {
                          if (
                            i === 1 || // Always show first page
                            i === totalPages || // Always show last page
                            (i >= currentPage - maxVisiblePages &&
                              i <= currentPage + maxVisiblePages) // Show pages in range
                          ) {
                            pages.push(
                              <PaginationItem key={i}>
                                <PaginationLink
                                  href="#"
                                  onClick={e => {
                                    e.preventDefault();
                                    handlePageChange(i);
                                  }}
                                  isActive={currentPage === i}
                                >
                                  {i}
                                </PaginationLink>
                              </PaginationItem>,
                            );
                          } else {
                            // left ellipsis
                            if (
                              i < currentPage - maxVisiblePages &&
                              !shouldShowLeftEllipsis
                            ) {
                              pages.push(
                                <PaginationItem key="left-ellipsis">
                                  <span className="px-2">...</span>
                                </PaginationItem>,
                              );
                              shouldShowLeftEllipsis = true;
                            }

                            // right ellipsis
                            if (
                              i > currentPage + maxVisiblePages &&
                              !shouldShowRightEllipsis
                            ) {
                              pages.push(
                                <PaginationItem key="right-ellipsis">
                                  <span className="px-2">...</span>
                                </PaginationItem>,
                              );
                              shouldShowRightEllipsis = true;
                            }
                          }
                        }
                        return pages;
                      })()}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            if (
                              currentPage <
                              Math.ceil(
                                data.pagination.totalCount / itemsPerPage,
                              )
                            )
                              handlePageChange(currentPage + 1);
                          }}
                          isActive={
                            currentPage <
                            Math.ceil(data.pagination.totalCount / itemsPerPage)
                          }
                          className={`${
                            currentPage <
                            Math.ceil(data.pagination.totalCount / itemsPerPage)
                              ? "cursor-pointer"
                              : "pointer-events-none"
                          }`}
                        >
                          <ChevronRight className="h-4 w-4 text-secondary" />
                        </PaginationNext>
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
};
